import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useNavigate } from 'react-router-dom';
import APIRequests from '../../../utility/APIRequests';
import Helper1, { capitalizeFirstLetter, notify } from '../../../utility/Helper1';
import { useSelector, useDispatch } from 'react-redux';
import { addUrlToPath } from '../../../utility/Helper1'
import { userSelect, allRemoteSettingsSelect, allLocalSettingsSelect } from '../../../redux/selectors/index';
import { authActions } from '../../../redux/actions';

import { WebLink, SiteWide, Images } from "../../../utility/customComponents";
import LoadingIcon from "../../../utility/customComponents/loader/LoadingIcon";
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Leaf, CheckCircle2, ArrowRight, CheckCircle } from "lucide-react"

import AuthHolder from "./AuthHolder";
import { AiFillCloseCircle, AiOutlineUser, AiOutlineMail, AiOutlineLock, AiOutlineEye, AiOutlineEyeInvisible, AiOutlineGlobal } from "react-icons/ai";


const Signin = ({ activeTab, setActiveTab }) => {

    const dispatch = useDispatch();
    const users = useSelector(userSelect);

    // Form states from original
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Loading and notification states from original
    const [loading, changeLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [notificationMessage, setNotificationMessage] = useState('');
    const [fullPageLoading, setFullPageLoading] = useState(false);
    
    // Focus state for modern UI
    const [focusedField, setFocusedField] = useState('');

    const navigate = useNavigate();

    const site_settings = useSelector(allRemoteSettingsSelect);
    const local_settings = useSelector(allLocalSettingsSelect);

    // Cookie management from original
    const deleteCookie = (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    }

    useEffect(() => {
        deleteCookie("news_already_read")
    }, [])

    // reCAPTCHA from original
    const [captcha, setCaptcha] = useState(null);

    const captchaHasChanged = (data) => {
        setCaptcha(data)
    }

    // Original login functionality
    const doLogin = async (e) => {
        e.preventDefault();

        if (!captcha && (Number(site_settings?.site_settings?.use_google_reCaptcha) == 1)) { 
            notify('Invalid captcha response', 0); 
            return; 
        }

        setFullPageLoading(true);
        if (loading) { return; }

        changeLoading(true)
        setLoadingText("Authenticating...")

        dispatch(authActions.onLogin("account/login", { 
            username: email, 
            captcha, 
            password: password 
        }, response => {
            if (response.code != 0) {
                if (response.code == 1) {
                    let toThisPlace;
                    if (local_settings?.lastPrivateUrl) {
                        toThisPlace = local_settings?.lastPrivateUrl
                    } else {
                        toThisPlace = '/dashboard'
                    }
                    navigate(toThisPlace, { replace: true });
                } else if (response.code === 2) {
                    navigate('/verify-user', { replace: true });
                }
            } else {
                notify(response.msg, response.code)
            }

            changeLoading(false)
            setLoadingText("")
            setFullPageLoading(false);
        }));
    };

    // User login style from original
    let userLoginStyle = site_settings?.operational_settings?.userLoginStyle
    let userLoginStyleObj = [
        {
            inputType: 'email',
            placeholder: 'Email',
        },
        {
            inputType: 'text',
            placeholder: 'Username',
        }
    ]

    return (
        <AuthHolder title="Sign In">
            {/* Full page loading overlay */}
            {fullPageLoading && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-gray-900/90 rounded-2xl p-8 border border-amber-500/20">
                        <div className="flex items-center space-x-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent"></div>
                            <span className="text-white font-medium">{loadingText}</span>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={doLogin} className="space-y-6">
                {/* Email/Username Field */}
                <div className="space-y-2">
                    <div className="relative">
                        <input
                            type={userLoginStyleObj?.[userLoginStyle]?.inputType || 'email'}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setFocusedField('signin-email')}
                            onBlur={() => setFocusedField('')}
                            placeholder={capitalizeFirstLetter(userLoginStyleObj?.[userLoginStyle]?.placeholder) || 'Email address'}
                            required
                            className="w-full h-14 pl-6 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                        />
                        <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                            focusedField === 'signin-email' || email ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                        }`}>
                            <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                        </div>
                    </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocusedField('signin-password')}
                            onBlur={() => setFocusedField('')}
                            placeholder="Password"
                            required
                            className="w-full h-14 pl-6 pr-14 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                        <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                            focusedField === 'signin-password' || password ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                        }`}>
                            <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                        </div>
                    </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between pt-2">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="hidden"
                        />
                        <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${
                            rememberMe 
                                ? 'bg-gradient-to-r from-amber-400 to-yellow-500 border-amber-500' 
                                : 'border-gray-500 group-hover:border-amber-400'
                        }`}>
                            {rememberMe && (
                                <CheckCircle size={12} className="text-black" />
                            )}
                        </div>
                        <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Keep me signed in</span>
                    </label>
                    <WebLink 
                        href="/reset"
                        className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                    >
                        Reset password
                    </WebLink>
                </div>

                {/* reCAPTCHA - Conditional rendering based on site settings */}
                {site_settings?.site_settings?.use_google_reCaptcha == 1 && (
                    <div className="pt-2">
                        <ReCAPTCHA onChange={captchaHasChanged} />
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-600 hover:to-amber-700 text-black font-semibold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
                >
                    <div className="flex items-center justify-center relative z-10">
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-3"></div>
                                {loadingText || "Signing in..."}
                            </>
                        ) : (
                            <>
                                Access Dashboard
                                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
                            </>
                        )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                </button>
            </form>
        </AuthHolder>
    );
};

export default Signin;